// Shared parser for WhatsApp template "components" (Meta / C3CRM format).
// Handles both the employee query (`components`) and the institute query
// (`template_form` + separate `template_header`). Returns the pieces needed to
// preview a template or rebuild it as a plain message.

export const MEDIA_FORMATS = ["IMAGE", "VIDEO", "DOCUMENT"];

const tryParse = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch (e) {
    return null;
  }
};

// Pull a usable media URL out of a header component / header object.
const pickUrl = (obj) => {
  if (!obj) return "";
  if (typeof obj === "string") return /^https?:\/\//i.test(obj) ? obj : "";
  return (
    (obj.example && obj.example.header_handle && obj.example.header_handle[0]) ||
    (obj.example && obj.example.header_url) ||
    obj.url ||
    obj.media_url ||
    obj.link ||
    (typeof obj.value === "string" && /^https?:\/\//i.test(obj.value) ? obj.value : "") ||
    ""
  );
};

/**
 * @param {Object} raw - a template row. May contain `components`, `template_form`
 *                        (array or JSON string) and/or `template_header`.
 * @returns {{ headerText, headerMedia: {type,url}|null, body, footer, buttons, paramCount }}
 */
export const parseWaTemplate = (raw) => {
  const result = {
    headerText: "",
    headerMedia: null,
    body: "",
    footer: "",
    buttons: [],
    paramCount: 0,
  };
  if (!raw) return result;

  let comps = tryParse(raw.components != null ? raw.components : raw.template_form);
  if (comps && !Array.isArray(comps) && Array.isArray(comps.components)) {
    comps = comps.components;
  }
  if (!Array.isArray(comps)) comps = [];

  comps.forEach((c) => {
    const type = String(c.type || "").toUpperCase();
    if (type === "HEADER") {
      const fmt = String(c.format || (c.text ? "TEXT" : "")).toUpperCase();
      if (fmt === "TEXT") {
        result.headerText = c.text || "";
      } else if (MEDIA_FORMATS.includes(fmt)) {
        result.headerMedia = { type: fmt.toLowerCase(), url: pickUrl(c) };
      }
    } else if (type === "BODY") {
      result.body = c.text || c.value || "";
    } else if (type === "FOOTER") {
      result.footer = c.text || "";
    } else if (type === "BUTTONS") {
      result.buttons = Array.isArray(c.buttons) ? c.buttons : [];
    }
  });

  // The institute query stores the header separately — use it to fill gaps
  // (especially the media URL, which template_form may not carry).
  const hdr = tryParse(raw.template_header);
  if (hdr) {
    const fmt = String((hdr && (hdr.format || hdr.type)) || "").toUpperCase();
    if (!result.headerMedia && MEDIA_FORMATS.includes(fmt)) {
      result.headerMedia = { type: fmt.toLowerCase(), url: pickUrl(hdr) };
    } else if (result.headerMedia && !result.headerMedia.url) {
      result.headerMedia.url = pickUrl(hdr) || result.headerMedia.url;
    }
    if (!result.headerText && fmt === "TEXT" && hdr.text) {
      result.headerText = hdr.text;
    }
  }

  const matches = (result.body || "").match(/\{\{\d+\}\}/g);
  result.paramCount = matches ? matches.length : 0;
  return result;
};

export const buttonToLine = (b) => {
  if (!b) return "";
  if (b.url) return `${b.text || "Link"}: ${b.url}`;
  if (b.phone_number) return `${b.text || "Call"}: ${b.phone_number}`;
  return b.text || "";
};
